import type {
  Agreement,
  Permit,
  ProductKind,
} from "$apis/parkito2/types.gen.ts";
import type { Agreement as AgreementOld } from "$apis/parkito/index.ts";
import {
  AgreementStatus as AgreementStatusEnum,
  type AgreementStatusType as AgreementStatus,
} from "./AgreementStatus.ts";
import { translateProductKind } from "../utils/misc.ts";
import { formatDate } from "../utils/dateTime.ts";
import { AgreementDataModel, DateModel, PermitModel } from "$models";

export default class AgreementModel implements Agreement {
  constructor(private _data: Agreement) {
  }

  static fromLegacy(data: AgreementOld): AgreementModel {
    const permit = data.permit as Permit;
    return new AgreementModel({ ...data, permit });
  }

  getStartDate(): DateModel | undefined {
    return this._data.data.startDate
      ? new DateModel(this._data.data.startDate)
      : undefined;
  }

  getState(): AgreementStatus {
    return this._data.state.stage;
    /*const offeredAt = this._data.offeredAt;
    const offerDeclinedAt = this._data.offerDeclinedAt;
    const approvedAt = this._data.approvedAt;
    const startDate = this._data.data.startDate;
    const endDate = this._data.data.endDate;
    const offerNoticePeriod = new IntervalModel(
      this._data.data.offerNoticePeriod,
    );

    // Approved, Active, Ending, Ended
    if (approvedAt && startDate) {
      // Start date is in the future
      if (Date.now() < new Date(startDate).getTime()) {
        return AgreementStatus.APPROVED;
      } // Start date is today or in the past
      else if (!endDate) {
        return AgreementStatus.ACTIVE;
      } // Started, End set but not passed
      else if (Date.now() <= new Date(endDate).getTime()) {
        return AgreementStatus.ENDING;
      } // Ended
      else {
        return AgreementStatus.ENDED;
      }
    } else {
      // Draft
      if (!offeredAt) {
        return AgreementStatus.DRAFT;
      } // Offer declined
      else if (offerDeclinedAt) {
        return AgreementStatus.OFFER_DECLINED;
      } // Offered and Offer expired
      else {
        const offeredDate = new Date(offeredAt);
        const expiresAfter = new Date(offeredDate);
        // Add offerNoticePeriod (days) to the offer date
        expiresAfter.setDate(
          expiresAfter.getDate() + offerNoticePeriod.days,
        );

        if (Date.now() <= expiresAfter.getTime()) {
          return AgreementStatus.OFFERED;
        } else {
          return AgreementStatus.OFFERED;
        }
      }
    }*/
  }

  isDraft(): boolean {
    return this.getState() === AgreementStatusEnum.DRAFT;
  }

  isEditable(): boolean {
    return this.isDraft();
  }

  isOffered(): boolean {
    return this.getState() === AgreementStatusEnum.OFFERED;
  }

  offeredAtDate(): Date | undefined {
    return this._data.offeredAt ? new Date(this._data.offeredAt) : undefined;
  }

  get offeredAt(): string | undefined {
    return this._data.offeredAt ?? undefined;
  }

  get data(): AgreementDataModel {
    return new AgreementDataModel(this._data.data);
  }

  offerExpiresDate(): Date | undefined {
    if (this.offeredAt) {
      const offeredAt = this.offeredAtDate();
      offeredAt?.setDate?.(
        offeredAt.getDate() + this.data.offerNoticePeriod.days,
      );
      return offeredAt;
    }
    return undefined;
  }

  offerDaysLeft(): number | undefined {
    const offerExpiresAt = this.offerExpiresDate();
    if (offerExpiresAt) {
      const msLeft = offerExpiresAt.valueOf() - new Date().valueOf();
      //const msLeft = new Date().valueOf() - new Date().valueOf();
      return Math.round(msLeft / 1000 / 60 / 60 / 24);
    }
  }

  isActive(): boolean {
    const state = this.getState();
    return state === AgreementStatusEnum.ACTIVE ||
      state === AgreementStatusEnum.ENDING;
  }
  isStarting(): boolean {
    const state = this.getState();
    return state === "approved";
  }

  isOfferCancellable(): boolean {
    const state = this.getState();
    return state === AgreementStatusEnum.OFFER_EXPIRED ||
      state === AgreementStatusEnum.OFFER_DECLINED ||
      state === AgreementStatusEnum.OFFERED;
  }

  isRemovable(): boolean {
    const state = this.getState();
    return state === AgreementStatusEnum.OFFER_EXPIRED ||
      state === AgreementStatusEnum.OFFER_DECLINED ||
      state === AgreementStatusEnum.DRAFT;
  }

  getPriceIndexText() {
    const priceIndexing = this.data.priceIndexing;
    if (priceIndexing) {
      if (priceIndexing.method === "baseIndex") {
        return "Basindex";
      } else {
        return `${this.getPriceIndexingType()} ${
          this.getPriceIndexingPercent() + "%"
        }`;
      }
    }
    return "Ej satt";
  }

  getPriceIndexingPercent(): number | undefined {
    const method = this.data.priceIndexing?.method;
    if (typeof method === "string" && method === "baseIndex") {
      return undefined;
    } else if (typeof method === "object") {
      if ("monthly" in method) {
        return method.monthly.percent;
      } else if ("quarterly" in method) {
        return method.quarterly.percent;
      } else if ("yearly" in method) {
        return method.yearly.percent;
      }
    }
    return undefined; // No valid type found
  }

  getPriceIndexingType():
    | "baseIndex"
    | "monthly"
    | "quarterly"
    | "yearly"
    | undefined {
    const method = this.data.priceIndexing?.method;
    if (typeof method === "string" && method === "baseIndex") {
      return "baseIndex";
    } else if (typeof method === "object") {
      if ("monthly" in method) {
        return "monthly";
      } else if ("quarterly" in method) {
        return "quarterly";
      } else if ("yearly" in method) {
        return "yearly";
      }
    }
    return undefined; // No valid type found
  }

  get derivedArticles() {
    return this._data.derivedArticles;
  }

  getArticleSum(includeVat: boolean = true): number {
    const derivedArticles = this.derivedArticles;
    let sum = 0;
    derivedArticles.forEach((article) => {
      sum += includeVat
        ? article.price.sub + article.price.vat
        : article.price.sub;
    });
    return sum;
  }

  translateAgreementState() {
    const state = this.getState();
    switch (state) {
      case "draft":
        return "Utkast";
      case "offered":
        return "Erbjudande offererat";
      case "offerExpired":
        return "Erbjudande förfallit";
      case "offerDeclined":
        return "Erbjudande avböjt";
      case "approved": {
        if (this.data.startDate) {
          return `Godkänt → ${formatDate(this.data.startDate, "YYYY-MM-DD")}`;
        } else {
          return "Godkänt";
        }
      }
      case "active":
        return "Aktivt";
      case "ending": {
        if (this.data.endDate) {
          return `Avslutas → ${formatDate(this.data.endDate, "YYYY-MM-DD")}`;
        } else {
          return "Avslutas";
        }
      }
      case "ended":
        return "Avslutat";
    }
  }

  getStateColor() {
    const state = this.getState();
    switch (state) {
      case "draft":
        return "text-yellow-50";
      case "offered":
        return "text-orange-50";
      case "offerExpired":
        return "text-red-5";
      case "offerDeclined":
        return "text-red-5";
      case "approved":
        return "text-green-39";
      case "active":
        return "text-green-39";
      case "ending":
        return "text-green-39";
      case "ended":
        return "text-grey-20";
    }
  }

  isPermit(): boolean {
    return !!this.permit;
  }

  getProductKind(): ProductKind {
    if (this.isPermit()) {
      return "Permit";
    } else {
      return "Reserved";
    }
  }

  translateProductKind(): string {
    return translateProductKind(this.getProductKind());
  }

  /**
   * This is slightly different from "transalte product kind"
   */
  translateContractType(): string {
    switch (this.getProductKind()) {
      case "Permit":
        return "Tillstånd";
      case "Reserved":
      case "Other":
        return "Avtal";
    }
  }

  getPermit(): PermitModel | undefined {
    return this.permit ? new PermitModel(this.permit) : undefined;
  }

  get permit(): PermitModel | undefined {
    return this._data.permit ? new PermitModel(this._data.permit) : undefined;
  }

  get createdAt() {
    return this._data.createdAt;
  }
  get updatedAt() {
    return this._data.updatedAt;
  }
  get approvedAt() {
    return this._data.approvedAt;
  }
  get supersedes() {
    return this._data.supersedes;
  }
  get supersededBy() {
    return this._data.supersededBy;
  }
  get wasPreApproved() {
    return this._data.wasPreApproved;
  }

  get debtor() {
    return this._data.debtor;
  }

  get events() {
    return this._data.events;
  }

  get id() {
    return this._data.id;
  }

  get number() {
    return this._data.number;
  }
  get spots() {
    return this._data.spots;
  }
  get state() {
    return this._data.state;
  }

  getUsedAccessCodes(): string[] {
    const codes = this._data.spots
      .map((s) => s.usedAccessCode)
      .filter((c) => c !== null && c !== undefined);
    return codes;
  }
}
